import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { toast } from '../hooks/use-toast';
import { ratingAPI } from '../services/api';

const RatingModal = ({ isOpen, onClose, serviceRequest, userRole, onRatingSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showTip, setShowTip] = useState(false);

  const handleSubmitRating = async () => {
    if (rating === 0) {
      toast({
        title: 'Rating Required',
        description: 'Please select a rating before submitting',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      await ratingAPI.submitRating(serviceRequest.id, rating, comment || null);
      toast({
        title: 'Rating Submitted!',
        description: 'Thank you for your feedback'
      });
      
      // Tipping is switched off until tips are charged through Stripe
      onRatingSubmitted();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit rating',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitTip = async () => {
    const amount = parseFloat(tipAmount);
    
    if (!amount || amount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid tip amount',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await ratingAPI.submitTip(serviceRequest.id, amount);
      toast({
        title: 'Tip Sent!',
        description: response.message,
        duration: 5000
      });
      onRatingSubmitted();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send tip',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderWheels = () => {
    return (
      <div className="flex gap-2 justify-center mb-4">
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setRating(value)}
            onMouseEnter={() => setHoverRating(value)}
            onMouseLeave={() => setHoverRating(0)}
            className="transition-transform hover:scale-110"
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill={(hoverRating || rating) >= value ? '#E17431' : 'none'}
              stroke={(hoverRating || rating) >= value ? '#E17431' : '#94a3b8'}
              strokeWidth="2"
              style={{ filter: (hoverRating || rating) >= value ? 'drop-shadow(0 0 8px rgba(225, 116, 49, 0.5))' : 'none' }}
            >
              {/* Wheel/Tire Icon */}
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="3" />
              <line x1="12" y1="3" x2="12" y2="6" />
              <line x1="12" y1="18" x2="12" y2="21" />
              <line x1="3" y1="12" x2="6" y2="12" />
              <line x1="18" y1="12" x2="21" y2="12" />
              <line x1="5.6" y1="5.6" x2="7.8" y2="7.8" />
              <line x1="16.2" y1="16.2" x2="18.4" y2="18.4" />
              <line x1="5.6" y1="18.4" x2="7.8" y2="16.2" />
              <line x1="16.2" y1="7.8" x2="18.4" y2="5.6" />
            </svg>
          </button>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent style={{ background: 'rgba(30, 58, 74, 0.95)', borderColor: 'rgba(46, 178, 213, 0.3)' }} className="text-white">
        {!showTip ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl text-white">
                Rate Your {userRole === 'customer' ? 'Driver' : 'Customer'}
              </DialogTitle>
              <DialogDescription className="text-gray-300">
                How was your experience with this service?
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <div>
                <Label className="text-white text-center block mb-3">
                  Select Rating (1-5 Wheels)
                </Label>
                {renderWheels()}
                {rating > 0 && (
                  <p className="text-center text-wheely-orange-light text-sm">
                    {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Needs Improvement'}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="comment" className="text-white">
                  Comment (Optional)
                </Label>
                <Textarea
                  id="comment"
                  placeholder="Share your experience..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  style={{ background: 'rgba(15, 23, 42, 0.8)', borderColor: 'rgba(46, 178, 213, 0.3)' }}
                  className="text-white resize-none"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSubmitRating}
                  disabled={submitting || rating === 0}
                  className="flex-1"
                  style={{ background: 'linear-gradient(to right, #E17431, #EB9868)' }}
                >
                  {submitting ? 'Submitting...' : 'Submit Rating'}
                </Button>
                <Button
                  variant="outline"
                  onClick={onClose}
                  style={{ borderColor: 'rgba(148, 163, 184, 0.5)', color: '#94a3b8' }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl text-white">
                💰 Tip Your Driver
              </DialogTitle>
              <DialogDescription className="text-gray-300">
                Show your appreciation! 100% of your tip goes directly to the driver.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <div className="bg-wheely-orange/20/10 border border-wheely-orange/30/30 rounded-lg p-4">
                <p className="text-wheely-orange-light text-sm">
                  <strong>No Commission:</strong> Your entire tip goes straight to the driver's wallet!
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tip" className="text-white">
                  Tip Amount (£)
                </Label>
                <Input
                  id="tip"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="Enter tip amount"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  style={{ background: 'rgba(15, 23, 42, 0.8)', borderColor: 'rgba(46, 178, 213, 0.3)' }}
                  className="text-white"
                />
                <p className="text-xs text-gray-400">Maximum tip: £100</p>
              </div>

              <div className="grid grid-cols-4 gap-2">
                {[2, 5, 10, 20].map((amount) => (
                  <Button
                    key={amount}
                    variant="outline"
                    onClick={() => setTipAmount(amount.toString())}
                    style={{ borderColor: 'rgba(46, 178, 213, 0.5)', color: '#2EB2D5' }}
                    className="hover:bg-wheely-orange/20/10"
                  >
                    £{amount}
                  </Button>
                ))}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSubmitTip}
                  disabled={submitting}
                  className="flex-1"
                  style={{ background: 'linear-gradient(to right, #10b981, #059669)' }}
                >
                  {submitting ? 'Processing...' : 'Send Tip'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    onRatingSubmitted();
                    onClose();
                  }}
                  style={{ borderColor: 'rgba(148, 163, 184, 0.5)', color: '#94a3b8' }}
                >
                  Skip
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RatingModal;
